import React, { useState, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import PageTitle from '@/components/common/PageTitle';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Messages } from '@/common/constants';
import Loading from '@/components/common/Loading';
import AnnounceService from '@/services/Announce.service';
import Announce from '@/types/Announce.type';
import DepartmentService from '@/services/Department.service';
import { Department, Departments } from '@/types/Department.type';
import HookFormControls, { HookFormControlsProps } from '@/components/common/HookFormControls';
import CommonActionButton from '@/components/common/CommonActionButton';
import URL from '@/common/routes/Url';
import dayjs from 'dayjs';

const AnnounceDetail: React.FC = () => {
  const pathParams = useParams<{ id: string }>();
  const useFormMethods = useForm<Announce & Departments>({ shouldUnregister: false });
  const {
    handleSubmit,
    getValues,
    formState: { isSubmitting },
    reset,
  } = useFormMethods;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [department, setDepartment] = useState<Department[] | []>([]);

  const title: HookFormControlsProps = {
    name: 'title',
    type: 'text',
    label: 'タイトル',
    rule: {
      required: 'タイトルは必須項目です。',
      maxLength: {
        value: 40,
        message: 'タイトルは40字以内で入力してください。',
      },
    },
  };

  const departments: HookFormControlsProps = {
    name: 'departments',
    type: 'autocomplete',
    label: '部署',
    options: department,
    getOptionLabel: 'name',
    autocompleteProps: {
      multiple: true,
    },
  };

  const startDate: HookFormControlsProps = {
    name: 'startDate',
    type: 'date',
    label: '公開開始日',
    rule: {
      required: '公開開始日は必須項目です。',
      pattern: {
        value: new RegExp('^[0-9]{4}(-|/)(0[1-9]|1[0-2])(-|/)(0[1-9]|[12][0-9]|3[01])$'),
        message: '公開開始日の形式が不正です。',
      },
    },
  };

  const endDate: HookFormControlsProps = {
    name: 'endDate',
    type: 'date',
    label: '公開終了日',
    InputLabelProps: { shrink: true },
    rule: {
      pattern: {
        value: new RegExp('^[0-9]{4}(-|/)(0[1-9]|1[0-2])(-|/)(0[1-9]|[12][0-9]|3[01])$'),
        message: '公開終了日の形式が不正です。',
      },
      validate: (value) =>
        !value || value >= getValues('startDate') || '公開終了日には、公開開始日以降の日付を指定してください。',
    },
  };

  const content: HookFormControlsProps = {
    name: 'content',
    type: 'textarea',
    label: 'アナウンス内容',
    rule: {
      required: 'アナウンス内容は必須項目です。',
      maxLength: {
        value: 500,
        message: 'アナウンス内容は500字以内で入力してください。',
      },
    },
  };

  useLayoutEffect(() => {
    (async () => {
      await DepartmentService.getAll().then((response: { data: Department[] }) => {
        setDepartment(response.data);
      });
      if (!pathParams.id) {
        setLoading(false);
        return;
      }
      await AnnounceService.get(pathParams.id)
        .then((response: { data: Announce }) => {
          response.data.startDate = response.data.startDate ? dayjs(response.data.startDate).format('YYYY-MM-DD') : '';
          response.data.endDate = response.data.endDate ? dayjs(response.data.endDate).format('YYYY-MM-DD') : '';

          reset(response.data);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          navigate(URL.announces);
          enqueueSnackbar(Messages.select.failure);
        });
      setLoading(false);
    })();
  }, []);

  const onSubmit = async (values: Announce & Departments) => {
    try {
      const departments = values.departments.map((department: Department) => {
        return department['id'];
      });
      const requestValues = { ...values, ...{ departments: departments } };

      if (pathParams.id) {
        await AnnounceService.update(requestValues, pathParams.id);
        enqueueSnackbar(Messages.update.success, { variant: 'info' });
      } else {
        await AnnounceService.create(requestValues);
        enqueueSnackbar(Messages.create.success, { variant: 'info' });
      }
      navigate(URL.announces);
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
      return;
    }
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={8} lg={4}>
        <Box sx= {{ display: { xs: 'block', sm: 'none' } }}>
          <PageTitle>アナウンス ー 編集</PageTitle>
        </Box>
        <Paper variant="outlined" sx={{ my: { xs: 0, md: 0 }, p: { xs: 2, md: 3 } }}>
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <HookFormControls {...title} autoComplete="off" />
                </Grid>
                <Grid item xs={12}>
                  <HookFormControls {...departments} />
                </Grid>
                <Grid item xs={12} display="flex" gap={2}>
                  <HookFormControls {...startDate} InputLabelProps={{ shrink: true }} autoComplete="off" />
                  <HookFormControls {...endDate} InputLabelProps={{ shrink: true }} autoComplete="off" />
                </Grid>
                <Grid item xs={12}>
                  <HookFormControls {...content} multiline rows={3} autoComplete="off" />
                </Grid>
                <Grid item xs={12}>
                  <CommonActionButton isSubmitting={isSubmitting} />
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Paper>
      </Grid>
    </Grid>
  );
};
export default AnnounceDetail;
